import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Play,
  Pause,
  RotateCw,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
  FastForward,
  Clock,
  Calendar,
  Bus,
  CheckCircle2,
  Sparkles,
  Zap,
} from 'lucide-react'
import HomeUnifiedHero, { buildHomeHeroMeta } from '../../../../src/components/home/HomeUnifiedHero'
import { ShuttleHeroCard, getRouteHeroState, getShuttleContext } from '../../../../src/components/ShuttleTab'
import {
  getShuttleRouteMotionLabel,
  getShuttleRouteMotionMode,
  getShuttleRouteMotionPhase,
} from '../../../../src/utils/shuttleRouteMotion'
import { getUnifiedShuttleRoutes } from '../../../../src/utils/shuttle/routeAdapter'
import { shuttleData } from '../../../../src/data/shuttleData'
import { formatSemesterWeek } from '../../../../src/utils/semesterUtils'
import './ShuttleVisualQa.css'

const VIEWPORT_PRESETS = [
  { id: '320', label: '320px (SE)', width: 320 },
  { id: '375', label: '375px (Mini)', width: 375 },
  { id: '390', label: '390px (Pro)', width: 390 },
  { id: '430', label: '430px (Max)', width: 430 },
  { id: 'full', label: '100% (402px)', width: 402 },
]

const SPEED_OPTIONS = [
  { value: 1, label: '1x' },
  { value: 10, label: '10x' },
  { value: 60, label: '60x' },
  { value: 300, label: '300x' },
]

const DATE_PRESETS = [
  { id: 'weekday', label: '학기중 평일', date: '2026-09-15' },
  { id: 'weekend', label: '주말 (일요일)', date: '2026-09-20' },
  { id: 'vacation', label: '방학 중', date: '2026-07-15' },
]

const DAY_MAP = ['일', '월', '화', '수', '목', '금', '토']

function timeToMin(timeStr) {
  if (!timeStr) return null
  const [h, m] = String(timeStr).split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
}

function timeToSec(timeStr) {
  if (!timeStr) return null
  const [h, m, s = 0] = String(timeStr).split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 3600 + m * 60 + (Number(s) || 0) : null
}

function minToTime(totalMin) {
  const safe = ((Math.round(totalMin) % 1440) + 1440) % 1440
  const hh = String(Math.floor(safe / 60)).padStart(2, '0')
  const mm = String(safe % 60).padStart(2, '0')
  return `${hh}:${mm}`
}

function secToTime(totalSec) {
  const safe = ((Math.floor(totalSec) % 86400) + 86400) % 86400
  const hh = String(Math.floor(safe / 3600)).padStart(2, '0')
  const mm = String(Math.floor((safe % 3600) / 60)).padStart(2, '0')
  const ss = String(safe % 60).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

function secToShortTime(totalSec) {
  const safe = ((Math.floor(totalSec) % 86400) + 86400) % 86400
  const hh = String(Math.floor(safe / 3600)).padStart(2, '0')
  const mm = String(Math.floor((safe % 3600) / 60)).padStart(2, '0')
  return `${hh}:${mm}`
}

function offsetTime(timeStr, offsetMinutes) {
  const min = timeToMin(timeStr)
  if (min == null) return timeStr
  return minToTime(min + offsetMinutes)
}

function createSimulatedDate(timeStr, { isWeekend = false, isSemesterOver = false } = {}) {
  let d = new Date(2026, 8, 15) // Month 8 is September
  if (isSemesterOver) {
    d = new Date(2026, 6, 15)
  } else if (isWeekend) {
    d = new Date(2026, 8, 20)
  }
  const [h, m] = (timeStr || '09:00').split(':').map(Number)
  d.setHours(h, m, 0, 0)
  return d
}

function buildDateFromDateStrAndSec(dateStr, totalSec) {
  const [y, m, d] = (dateStr || '2026-09-15').split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const safe = ((Math.floor(totalSec) % 86400) + 86400) % 86400
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  date.setHours(hours, minutes, seconds, 0)
  return date
}

export function generateRouteVisualScenarios(route) {
  if (!route) return []
  const schedules = route.schedules ?? []
  const scenarios = []

  if (schedules.length === 0) {
    scenarios.push({
      id: 'ended-no-schedule',
      title: '운행 없음 / 종료',
      description: '등록된 시간표가 없는 상태',
      simulatedTime: '12:00',
      dateType: 'weekday',
    })
    scenarios.push({
      id: 'weekend',
      title: '주말 휴무',
      description: '주말 운행 없음 안내',
      simulatedTime: '10:00',
      dateType: 'weekend',
    })
    return scenarios
  }

  const firstSchedule = schedules[0]
  const firstTime = firstSchedule.time

  // 1. Idle
  scenarios.push({
    id: `${firstSchedule.id || 'first'}-idle`,
    title: 'Idle (대기)',
    description: `첫차 ${firstTime} 기준 15분 전`,
    simulatedTime: offsetTime(firstTime, -15),
    targetDepartureTime: firstTime,
    dateType: 'weekday',
  })

  // 2. Approaching 5m
  scenarios.push({
    id: `${firstSchedule.id || 'first'}-approaching-5m`,
    title: 'Approaching 5m (접근)',
    description: `${firstTime} 출발 5분 전 (접근 펄스)`,
    simulatedTime: offsetTime(firstTime, -5),
    targetDepartureTime: firstTime,
    dateType: 'weekday',
  })

  // 3. Approaching 1m
  scenarios.push({
    id: `${firstSchedule.id || 'first'}-approaching-1m`,
    title: 'Approaching 1m (직전)',
    description: `${firstTime} 출발 1분 전`,
    simulatedTime: offsetTime(firstTime, -1),
    targetDepartureTime: firstTime,
    dateType: 'weekday',
  })

  // 4. Departing
  scenarios.push({
    id: `${firstSchedule.id || 'first'}-departing`,
    title: 'Departing (출발)',
    description: `${firstTime} 정시 출발 애니메이션`,
    simulatedTime: firstTime,
    targetDepartureTime: firstTime,
    dateType: 'weekday',
  })

  // 5. Cluster 배차 탐색
  const clusterIndex = schedules.findIndex((s, idx) => {
    if (idx >= schedules.length - 1) return false
    const currentMin = timeToMin(s.time)
    const nextMin = timeToMin(schedules[idx + 1].time)
    return currentMin != null && nextMin != null && nextMin - currentMin > 0 && nextMin - currentMin <= 15
  })

  if (clusterIndex !== -1) {
    const clusterSchedule = schedules[clusterIndex]
    scenarios.push({
      id: `${clusterSchedule.id}-cluster-approaching`,
      title: 'Cluster Approaching (연속 배차 접근)',
      description: `${clusterSchedule.time} 출발 3분 전 · 다음 ${schedules[clusterIndex + 1].time} 배차 연계`,
      simulatedTime: offsetTime(clusterSchedule.time, -3),
      targetDepartureTime: clusterSchedule.time,
      dateType: 'weekday',
    })

    scenarios.push({
      id: `${clusterSchedule.id}-cluster-departing`,
      title: 'Cluster Departing (연속 배차 출발)',
      description: `${clusterSchedule.time} 출발 · 다음 ${schedules[clusterIndex + 1].time} 배차로 handoff`,
      simulatedTime: clusterSchedule.time,
      targetDepartureTime: clusterSchedule.time,
      dateType: 'weekday',
    })
  }

  // 6. 다대 운행 배차 탐색
  const multiBusSchedule = schedules.find(s => (s.buses ?? 1) > 1)
  if (multiBusSchedule) {
    scenarios.push({
      id: `${multiBusSchedule.id}-multibus-approaching`,
      title: `Multi-Bus Approaching (${multiBusSchedule.buses}대 운행)`,
      description: `${multiBusSchedule.time} · ${multiBusSchedule.buses}대 동시 배차 접근 펄스`,
      simulatedTime: offsetTime(multiBusSchedule.time, -3),
      targetDepartureTime: multiBusSchedule.time,
      dateType: 'weekday',
    })

    scenarios.push({
      id: `${multiBusSchedule.id}-multibus-departing`,
      title: `Multi-Bus Departing (${multiBusSchedule.buses}대 출발)`,
      description: `${multiBusSchedule.time} · ${multiBusSchedule.buses}대 다중 펄스 출발`,
      simulatedTime: multiBusSchedule.time,
      targetDepartureTime: multiBusSchedule.time,
      dateType: 'weekday',
    })
  }

  // 7. 긴 공백 단일 배차
  const longGapIndex = schedules.findIndex((s, idx) => {
    if (idx === 0) return false
    const prevMin = timeToMin(schedules[idx - 1].time)
    const currentMin = timeToMin(s.time)
    return prevMin != null && currentMin != null && currentMin - prevMin > 20
  })

  if (longGapIndex !== -1) {
    const longGapSchedule = schedules[longGapIndex]
    scenarios.push({
      id: `${longGapSchedule.id}-longgap-idle`,
      title: 'Single Long-Gap Idle (단일 장시간 대기)',
      description: `${longGapSchedule.time} 기준 35분 전 여유 대기`,
      simulatedTime: offsetTime(longGapSchedule.time, -35),
      targetDepartureTime: longGapSchedule.time,
      dateType: 'weekday',
    })
  }

  // 8. 당일 운행 종료
  scenarios.push({
    id: 'ended-today',
    title: 'Ended (당일 운행 종료)',
    description: '모든 배차 시간 경과 후 (21:30)',
    simulatedTime: '21:30',
    dateType: 'weekday',
  })

  // 9. 주말 운행 없음
  scenarios.push({
    id: 'weekend',
    title: 'Weekend (주말 운행 없음)',
    description: '일요일 주말 휴무 상태',
    simulatedTime: '10:00',
    dateType: 'weekend',
  })

  // 10. 학기 운행 종료
  scenarios.push({
    id: 'semester-ended',
    title: 'Semester End (학기 운행 종료/방학)',
    description: '학기 운행 기간 외 (7월 중순)',
    simulatedTime: '10:00',
    dateType: 'semesterOver',
  })

  return scenarios
}

function ScenarioCard({
  scenario,
  route,
  targetView,
  isDark,
  viewportWidth,
  replayEpoch,
}) {
  const [localReplayKey, setLocalReplayKey] = useState(0)
  const currentReplayKey = `${replayEpoch}-${localReplayKey}`

  const simulatedDate = useMemo(() => {
    return createSimulatedDate(scenario.simulatedTime, {
      isWeekend: scenario.dateType === 'weekend',
      isSemesterOver: scenario.dateType === 'semesterOver',
    })
  }, [scenario.dateType, scenario.simulatedTime])

  const simulatedNowMin = simulatedDate.getHours() * 60 + simulatedDate.getMinutes()

  const context = useMemo(() => getShuttleContext(simulatedDate), [simulatedDate])
  const heroState = useMemo(() => getRouteHeroState(route, simulatedNowMin, context), [context, route, simulatedNowMin])

  const nextSchedule = heroState?.nextSchedule ?? null
  const next = heroState?.next ?? null
  const departureTime = nextSchedule?.time ?? null
  const minsUntilDeparture = next?.minutesLeft ?? null
  const busCount = nextSchedule?.buses ?? 1

  const followingSchedule = useMemo(() => {
    if (!departureTime || !route?.schedules) return null
    return route.schedules.find(s => {
      const sMin = timeToMin(s.time)
      const curMin = timeToMin(departureTime)
      return sMin != null && curMin != null && sMin > curMin
    }) ?? null
  }, [departureTime, route?.schedules])

  const isEnded = Boolean(context?.isWeekend || context?.isSemesterOver || !nextSchedule || !next)
  const visualPhase = getShuttleRouteMotionPhase({
    departureTime,
    minsUntilDeparture,
    isEnded,
  })
  const motionMode = getShuttleRouteMotionMode({
    departureTime,
    followingDepartureTime: followingSchedule?.time ?? null,
  })
  const phaseLabel = getShuttleRouteMotionLabel(visualPhase)

  const dateStr = `${simulatedDate.getMonth() + 1}월 ${simulatedDate.getDate()}일 (${DAY_MAP[simulatedDate.getDay()]})`
  const weekStr = formatSemesterWeek(simulatedDate)

  const homeStatusLabel = context.isSemesterOver
    ? '학기 운행이 종료됐어요'
    : context.isWeekend
      ? '오늘은 셔틀이 없어요'
      : minsUntilDeparture == null
        ? '오늘 운행이 종료됐어요'
        : context.direction === 'fromSchool'
          ? '이제 집에 갈 시간이에요'
          : '셔틀이 곧 출발해요'

  return (
    <article className={`shuttle-qa-scenario-card${isDark ? ' is-dark' : ''}`} aria-labelledby={`qa-scenario-title-${scenario.id}`}>
      <header className="shuttle-qa-scenario-header">
        <div className="shuttle-qa-scenario-meta">
          <span className={`shuttle-qa-phase-badge phase-${visualPhase}`} title="Visual Phase">
            {visualPhase.toUpperCase()} · {phaseLabel}
          </span>
          <span className="shuttle-qa-mode-badge" title="Motion Mode">
            {motionMode.toUpperCase()}
          </span>
          {departureTime && (
            <span className="shuttle-qa-dep-badge" title="Departure Time">
              {departureTime}
            </span>
          )}
          {minsUntilDeparture != null && (
            <span className="shuttle-qa-mins-badge" title="Minutes to Departure">
              {minsUntilDeparture}분 남음
            </span>
          )}
          {busCount > 1 && (
            <span className="shuttle-qa-buscount-badge" title="Bus Count">
              {busCount}대 운행
            </span>
          )}
          <span className="shuttle-qa-sim-badge" title="Simulated Time">
            Sim: {scenario.simulatedTime} ({scenario.dateType})
          </span>
        </div>

        <div className="shuttle-qa-scenario-actions">
          <button
            type="button"
            className="shuttle-qa-replay-btn"
            title="애니메이션 다시 재생"
            onClick={() => setLocalReplayKey(k => k + 1)}
          >
            <RotateCw size={13} />
            <span>Replay</span>
          </button>
        </div>
      </header>

      <div className="shuttle-qa-scenario-info">
        <strong id={`qa-scenario-title-${scenario.id}`} className="shuttle-qa-scenario-title">
          {scenario.title}
        </strong>
        <span className="shuttle-qa-scenario-desc">{scenario.description}</span>
      </div>

      <div className="shuttle-qa-preview-grid" data-view={targetView}>
        {(targetView === 'both' || targetView === 'home') && (
          <div className="shuttle-qa-preview-cell" style={{ width: `${viewportWidth}px` }}>
            <div className="shuttle-qa-preview-cell-header">
              <span className="shuttle-qa-tag home">Home Shuttle Hero</span>
            </div>
            <div className="shuttle-qa-preview-shell" key={`home-${scenario.id}-${currentReplayKey}`}>
              <HomeUnifiedHero
                isDark={isDark}
                motionMode="shuttle"
                variant="data"
                urgent={minsUntilDeparture != null && minsUntilDeparture <= 5}
                metaLine={buildHomeHeroMeta({ dateStr, weekStr, profile: null, previewMode: true })}
                displayName="예성"
                statusLabel={homeStatusLabel}
                subLine={route.name}
                ctaLabel="셔틀 보기"
                shuttleTime={departureTime}
                minsToShuttle={minsUntilDeparture}
                shuttleRouteName={route.name}
                shuttleBusCount={busCount}
                shuttleFollowingTime={followingSchedule?.time ?? null}
                onClick={() => {}}
              />
            </div>
          </div>
        )}

        {(targetView === 'both' || targetView === 'shuttle') && (
          <div className="shuttle-qa-preview-cell" style={{ width: `${viewportWidth}px` }}>
            <div className="shuttle-qa-preview-cell-header">
              <span className="shuttle-qa-tag shuttle">Shuttle Page Hero</span>
            </div>
            <div className="shuttle-qa-preview-shell" key={`shuttle-${scenario.id}-${currentReplayKey}`}>
              <ShuttleHeroCard
                hero={heroState}
                isDark={isDark}
                isPreview={true}
                onOpenDetail={() => {}}
              />
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

export default function ShuttleVisualQa() {
  const unifiedRoutes = useMemo(() => getUnifiedShuttleRoutes(shuttleData?.routes ?? []), [])
  const [selectedRouteId, setSelectedRouteId] = useState(() => {
    return unifiedRoutes.find(r => r.id === 'byeongjeom_to_school')?.id || unifiedRoutes[0]?.id || ''
  })
  const [targetView, setTargetView] = useState('both') // 'both' | 'home' | 'shuttle'
  const [viewportPreset, setViewportPreset] = useState('full')
  const [isDark, setIsDark] = useState(false)
  const [replayEpoch, setReplayEpoch] = useState(0)
  const [isLegacyOpen, setIsLegacyOpen] = useState(false)

  // ── Time Machine States ────────────────────────────
  const [selectedDateStr, setSelectedDateStr] = useState('2026-09-15')
  // Default to 08:39:42 (31182 seconds)
  const [simulatedSeconds, setSimulatedSeconds] = useState(8 * 3600 + 39 * 60 + 42)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)

  const selectedRoute = useMemo(() => {
    return unifiedRoutes.find(r => r.id === selectedRouteId) || unifiedRoutes[0] || null
  }, [selectedRouteId, unifiedRoutes])

  // Timeline boundaries (e.g. 06:00:00 to 23:00:00, or expanded based on route)
  const { timelineMinSec, timelineMaxSec } = useMemo(() => {
    let minSec = 6 * 3600
    let maxSec = 23 * 3600
    if (selectedRoute?.schedules?.length) {
      const firstSec = timeToSec(selectedRoute.schedules[0].time)
      const lastSec = timeToSec(selectedRoute.schedules[selectedRoute.schedules.length - 1].time)
      if (firstSec != null && firstSec - 1800 < minSec) minSec = Math.max(0, firstSec - 1800)
      if (lastSec != null && lastSec + 3600 > maxSec) maxSec = Math.min(86399, lastSec + 3600)
    }
    return { timelineMinSec: minSec, timelineMaxSec: maxSec }
  }, [selectedRoute])

  // Single Simulation Clock Loop
  const lastTimeRef = useRef(null)
  const animFrameRef = useRef(null)

  const stopPlayback = useCallback(() => {
    setIsPlaying(false)
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current)
      animFrameRef.current = null
    }
    lastTimeRef.current = null
  }, [])

  useEffect(() => {
    if (!isPlaying) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
      lastTimeRef.current = null
      return
    }

    const step = timestamp => {
      if (lastTimeRef.current == null) {
        lastTimeRef.current = timestamp
      }
      const dt = (timestamp - lastTimeRef.current) / 1000 // seconds
      lastTimeRef.current = timestamp

      setSimulatedSeconds(prev => {
        const nextSec = prev + dt * playbackSpeed
        if (nextSec >= timelineMaxSec) {
          stopPlayback()
          return timelineMaxSec
        }
        return nextSec
      })

      animFrameRef.current = requestAnimationFrame(step)
    }

    animFrameRef.current = requestAnimationFrame(step)

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        animFrameRef.current = null
      }
      lastTimeRef.current = null
    }
  }, [isPlaying, playbackSpeed, stopPlayback, timelineMaxSec])

  const handleRouteChange = newRouteId => {
    setSelectedRouteId(newRouteId)
  }

  const handleDateChange = newDateStr => {
    setSelectedDateStr(newDateStr)
  }

  const handleStepSeconds = deltaSec => {
    setSimulatedSeconds(prev => Math.max(timelineMinSec, Math.min(timelineMaxSec, prev + deltaSec)))
  }

  const handleSliderChange = e => {
    const val = Number(e.target.value)
    setSimulatedSeconds(val)
  }

  const handleMarkerClick = depSec => {
    setSimulatedSeconds(depSec)
  }

  // ── Compute Live Simulated Production State ──────────
  const currentSimulatedDate = useMemo(() => {
    return buildDateFromDateStrAndSec(selectedDateStr, simulatedSeconds)
  }, [selectedDateStr, simulatedSeconds])

  const simulatedNowMin = currentSimulatedDate.getHours() * 60 + currentSimulatedDate.getMinutes()

  // 1. Production Context & Route Hero State
  const liveContext = useMemo(() => getShuttleContext(currentSimulatedDate), [currentSimulatedDate])
  const liveHeroState = useMemo(() => getRouteHeroState(selectedRoute, simulatedNowMin, liveContext), [liveContext, selectedRoute, simulatedNowMin])

  const liveNextSchedule = liveHeroState?.nextSchedule ?? null
  const liveNext = liveHeroState?.next ?? null
  const liveDepartureTime = liveNextSchedule?.time ?? null
  const liveMinsUntilDeparture = liveNext?.minutesLeft ?? null
  const liveBusCount = liveNextSchedule?.buses ?? 1

  const liveFollowingSchedule = useMemo(() => {
    if (!liveDepartureTime || !selectedRoute?.schedules) return null
    return selectedRoute.schedules.find(s => {
      const sMin = timeToMin(s.time)
      const curMin = timeToMin(liveDepartureTime)
      return sMin != null && curMin != null && sMin > curMin
    }) ?? null
  }, [liveDepartureTime, selectedRoute?.schedules])

  const isLiveEnded = Boolean(liveContext?.isWeekend || liveContext?.isSemesterOver || !liveNextSchedule || !liveNext)
  const liveVisualPhase = getShuttleRouteMotionPhase({
    departureTime: liveDepartureTime,
    minsUntilDeparture: liveMinsUntilDeparture,
    isEnded: isLiveEnded,
  })
  const liveMotionMode = getShuttleRouteMotionMode({
    departureTime: liveDepartureTime,
    followingDepartureTime: liveFollowingSchedule?.time ?? null,
  })
  const livePhaseLabel = getShuttleRouteMotionLabel(liveVisualPhase)

  // 2. Production Home Hero Data
  const dateStr = `${currentSimulatedDate.getMonth() + 1}월 ${currentSimulatedDate.getDate()}일 (${DAY_MAP[currentSimulatedDate.getDay()]})`
  const weekStr = formatSemesterWeek(currentSimulatedDate)

  const homeStatusLabel = liveContext.isSemesterOver
    ? '학기 운행이 종료됐어요'
    : liveContext.isWeekend
      ? '오늘은 셔틀이 없어요'
      : liveMinsUntilDeparture == null
        ? '오늘 운행이 종료됐어요'
        : liveContext.direction === 'fromSchool'
          ? '이제 집에 갈 시간이에요'
          : '셔틀이 곧 출발해요'

  // Time until departure in seconds
  const liveSecondsUntilDeparture = useMemo(() => {
    if (!liveDepartureTime || isLiveEnded) return null
    const depSec = timeToSec(liveDepartureTime)
    if (depSec == null) return null
    const curSec = Math.floor(simulatedSeconds)
    return depSec - curSec
  }, [isLiveEnded, liveDepartureTime, simulatedSeconds])

  // Dynamic Checkpoints for Quick Jump
  const dynamicCheckpoints = useMemo(() => {
    if (!selectedRoute?.schedules?.length) return []
    const schedules = selectedRoute.schedules
    const list = []
    const firstSchedule = schedules[0]
    const firstSec = timeToSec(firstSchedule.time)

    if (firstSec != null) {
      list.push({ label: `첫차 -5분`, timeSec: Math.max(timelineMinSec, firstSec - 300) })
      list.push({ label: `첫차 -1분`, timeSec: Math.max(timelineMinSec, firstSec - 60) })
      list.push({ label: `첫차 (${firstSchedule.time})`, timeSec: firstSec })
    }

    // Next upcoming schedule
    if (liveDepartureTime) {
      const depSec = timeToSec(liveDepartureTime)
      if (depSec != null && depSec !== firstSec) {
        list.push({ label: `다음 (${liveDepartureTime})`, timeSec: depSec })
      }
    }

    // Multi-bus checkpoint
    const multiBus = schedules.find(s => (s.buses ?? 1) > 1)
    if (multiBus) {
      const mSec = timeToSec(multiBus.time)
      if (mSec != null) {
        list.push({ label: `다중배차 (${multiBus.time} ×${multiBus.buses})`, timeSec: mSec })
      }
    }

    // Cluster checkpoint (interval <= 15 min)
    const cluster = schedules.find((s, i) => {
      if (i >= schedules.length - 1) return false
      const cMin = timeToMin(s.time)
      const nMin = timeToMin(schedules[i + 1].time)
      return cMin != null && nMin != null && nMin - cMin > 0 && nMin - cMin <= 15
    })
    if (cluster) {
      const cSec = timeToSec(cluster.time)
      if (cSec != null) {
        list.push({ label: `연속배차 (${cluster.time})`, timeSec: cSec })
      }
    }

    // Last schedule
    const lastSchedule = schedules[schedules.length - 1]
    const lastSec = timeToSec(lastSchedule.time)
    if (lastSec != null && lastSec !== firstSec) {
      list.push({ label: `막차 (${lastSchedule.time})`, timeSec: lastSec })
      list.push({ label: `막차 이후`, timeSec: Math.min(timelineMaxSec, lastSec + 900) })
    }

    return list
  }, [liveDepartureTime, selectedRoute?.schedules, timelineMaxSec, timelineMinSec])

  // Legacy Scenarios for Collapsible section
  const scenarios = useMemo(() => {
    return generateRouteVisualScenarios(selectedRoute)
  }, [selectedRoute])

  const currentViewportWidth = useMemo(() => {
    const preset = VIEWPORT_PRESETS.find(p => p.id === viewportPreset)
    return preset ? preset.width : 402
  }, [viewportPreset])

  // Slider percentage for scrubber track
  const sliderPercentage = useMemo(() => {
    const clamped = Math.max(timelineMinSec, Math.min(timelineMaxSec, simulatedSeconds))
    return ((clamped - timelineMinSec) / (timelineMaxSec - timelineMinSec)) * 100
  }, [simulatedSeconds, timelineMaxSec, timelineMinSec])

  return (
    <section className={`shuttle-visual-qa-root${isDark ? ' is-dark' : ''}`} aria-label="셔틀 Visual QA 시스템">
      {/* ── Top Main Toolbar ──────────────────────────── */}
      <header className="shuttle-qa-toolbar">
        <div className="shuttle-qa-toolbar-row">
          <div className="shuttle-qa-control-group">
            <label htmlFor="shuttle-qa-route-select" className="shuttle-qa-control-label">
              <Bus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              노선 선택
            </label>
            <select
              id="shuttle-qa-route-select"
              className="shuttle-qa-select"
              value={selectedRouteId}
              onChange={e => handleRouteChange(e.target.value)}
            >
              {unifiedRoutes.map(route => (
                <option key={route.id} value={route.id}>
                  {route.name} ({route.schedules?.length ?? 0}개 배차)
                </option>
              ))}
            </select>
          </div>

          <div className="shuttle-qa-control-group">
            <span className="shuttle-qa-control-label">비교 뷰</span>
            <div className="shuttle-qa-btn-group" role="group" aria-label="비교 뷰 선택">
              <button
                type="button"
                className={`shuttle-qa-btn${targetView === 'both' ? ' active' : ''}`}
                onClick={() => setTargetView('both')}
              >
                Both (Home & Shuttle)
              </button>
              <button
                type="button"
                className={`shuttle-qa-btn${targetView === 'home' ? ' active' : ''}`}
                onClick={() => setTargetView('home')}
              >
                Home Hero
              </button>
              <button
                type="button"
                className={`shuttle-qa-btn${targetView === 'shuttle' ? ' active' : ''}`}
                onClick={() => setTargetView('shuttle')}
              >
                Shuttle Page Hero
              </button>
            </div>
          </div>

          <div className="shuttle-qa-control-group">
            <span className="shuttle-qa-control-label">뷰포트 너비</span>
            <div className="shuttle-qa-btn-group" role="group" aria-label="뷰포트 너비 선택">
              {VIEWPORT_PRESETS.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  className={`shuttle-qa-btn${viewportPreset === preset.id ? ' active' : ''}`}
                  onClick={() => setViewportPreset(preset.id)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="shuttle-qa-toolbar-actions">
            <button
              type="button"
              className={`shuttle-qa-icon-btn${isDark ? ' active' : ''}`}
              title={isDark ? '라이트 모드로 전환' : '다크 모드로 전환'}
              onClick={() => setIsDark(d => !d)}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
              <span>{isDark ? 'Light' : 'Dark'}</span>
            </button>

            <button
              type="button"
              className="shuttle-qa-replay-all-btn"
              title="현재 시각 애니메이션 다시 재생"
              onClick={() => setReplayEpoch(e => e + 1)}
            >
              <RotateCw size={14} />
              <span>Replay</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Main Interactive Time Machine Studio ──────────── */}
      <main className="shuttle-tm-container">
        {/* Left / Center: Live Production Hero Preview */}
        <section className="shuttle-tm-stage-area" aria-label="실시간 프로덕션 히어로 프리뷰">
          <div className="shuttle-tm-stage-header">
            <div className="shuttle-tm-route-badge">
              <Sparkles size={14} className="shuttle-tm-sparkle" />
              <strong>{selectedRoute?.name}</strong>
              <span className="shuttle-tm-route-stops">
                ({selectedRoute?.from || selectedRoute?.boardingPoint || '출발'} → {selectedRoute?.to || selectedRoute?.destinationLabel || '도착'})
              </span>
            </div>
            <div className="shuttle-tm-stage-status">
              <span className={`shuttle-qa-phase-badge phase-${liveVisualPhase}`}>
                {liveVisualPhase.toUpperCase()} · {livePhaseLabel}
              </span>
              <span className="shuttle-qa-mode-badge">{liveMotionMode.toUpperCase()}</span>
              {liveDepartureTime && (
                <span className="shuttle-qa-dep-badge">{liveDepartureTime}</span>
              )}
              {liveBusCount > 1 && (
                <span className="shuttle-qa-buscount-badge">{liveBusCount}대 동시운행</span>
              )}
            </div>
          </div>

          <div className="shuttle-qa-preview-grid" data-view={targetView}>
            {(targetView === 'both' || targetView === 'home') && (
              <div className="shuttle-qa-preview-cell" style={{ width: `${currentViewportWidth}px` }}>
                <div className="shuttle-qa-preview-cell-header">
                  <span className="shuttle-qa-tag home">Home Shuttle Hero</span>
                  <span className="shuttle-tm-sync-tag">Production Live</span>
                </div>
                <div className="shuttle-qa-preview-shell" key={`home-live-${selectedRouteId}-${replayEpoch}`}>
                  <HomeUnifiedHero
                    isDark={isDark}
                    motionMode="shuttle"
                    variant="data"
                    urgent={liveMinsUntilDeparture != null && liveMinsUntilDeparture <= 5}
                    metaLine={buildHomeHeroMeta({ dateStr, weekStr, profile: null, previewMode: true })}
                    displayName="예성"
                    statusLabel={homeStatusLabel}
                    subLine={selectedRoute?.name}
                    ctaLabel="셔틀 보기"
                    shuttleTime={liveDepartureTime}
                    minsToShuttle={liveMinsUntilDeparture}
                    shuttleRouteName={selectedRoute?.name}
                    shuttleBusCount={liveBusCount}
                    shuttleFollowingTime={liveFollowingSchedule?.time ?? null}
                    onClick={() => {}}
                  />
                </div>
              </div>
            )}

            {(targetView === 'both' || targetView === 'shuttle') && (
              <div className="shuttle-qa-preview-cell" style={{ width: `${currentViewportWidth}px` }}>
                <div className="shuttle-qa-preview-cell-header">
                  <span className="shuttle-qa-tag shuttle">Shuttle Page Hero</span>
                  <span className="shuttle-tm-sync-tag">Production Live</span>
                </div>
                <div className="shuttle-qa-preview-shell" key={`shuttle-live-${selectedRouteId}-${replayEpoch}`}>
                  <ShuttleHeroCard
                    hero={liveHeroState}
                    isDark={isDark}
                    isPreview={true}
                    onOpenDetail={() => {}}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Right: Time Machine Controller & Status Panel */}
        <aside className="shuttle-tm-controller-panel" aria-label="시간 컨트롤러 패널">
          <div className="shuttle-tm-panel-card">
            <div className="shuttle-tm-panel-title-row">
              <h2 className="shuttle-tm-panel-title">
                <Clock size={16} />
                셔틀 타임머신
              </h2>
              <span className="shuttle-tm-clock-digital" aria-label="시뮬레이션 현재 시각">
                {secToTime(simulatedSeconds)}
              </span>
            </div>

            {/* Date Selection Control */}
            <div className="shuttle-tm-control-section">
              <label htmlFor="shuttle-tm-date-input" className="shuttle-tm-section-label">
                <Calendar size={13} />
                운행 기준 날짜
              </label>
              <div className="shuttle-tm-date-row">
                <input
                  id="shuttle-tm-date-input"
                  type="date"
                  className="shuttle-tm-date-input"
                  value={selectedDateStr}
                  onChange={e => handleDateChange(e.target.value)}
                />
                <div className="shuttle-tm-date-presets">
                  {DATE_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`shuttle-tm-chip-btn${selectedDateStr === preset.date ? ' active' : ''}`}
                      onClick={() => handleDateChange(preset.date)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Timeline Slider with Official Departure Markers */}
            <div className="shuttle-tm-control-section">
              <div className="shuttle-tm-slider-header">
                <span className="shuttle-tm-time-bound">{secToShortTime(timelineMinSec)}</span>
                <span className="shuttle-tm-slider-center-time">{secToTime(simulatedSeconds)}</span>
                <span className="shuttle-tm-time-bound">{secToShortTime(timelineMaxSec)}</span>
              </div>

              <div className="shuttle-tm-slider-container">
                <div className="shuttle-tm-track-wrapper">
                  <input
                    type="range"
                    className="shuttle-tm-slider"
                    min={timelineMinSec}
                    max={timelineMaxSec}
                    step={1}
                    value={Math.floor(simulatedSeconds)}
                    onChange={handleSliderChange}
                    aria-label="하루 전체 시간 슬라이더"
                  />
                  <div
                    className="shuttle-tm-progress-bar"
                    style={{ width: `${sliderPercentage}%` }}
                    aria-hidden="true"
                  />
                </div>

                {/* Departure Event Markers on Timeline */}
                <div className="shuttle-tm-markers-rail" aria-label="공식 배차 마커">
                  {(selectedRoute?.schedules ?? []).map(schedule => {
                    const sSec = timeToSec(schedule.time)
                    if (sSec == null || sSec < timelineMinSec || sSec > timelineMaxSec) return null
                    const percent = ((sSec - timelineMinSec) / (timelineMaxSec - timelineMinSec)) * 100
                    const isCurrent = liveDepartureTime === schedule.time
                    const isPassed = simulatedSeconds >= sSec
                    const count = schedule.buses ?? 1

                    return (
                      <button
                        key={schedule.id || schedule.time}
                        type="button"
                        className={`shuttle-tm-marker-pin${isCurrent ? ' is-current' : ''}${isPassed ? ' is-passed' : ''}`}
                        style={{ left: `${percent}%` }}
                        title={`${schedule.time}${count > 1 ? ` (${count}대)` : ''} 배차로 이동`}
                        onClick={() => handleMarkerClick(sSec)}
                      >
                        <span className="shuttle-tm-marker-dot" />
                        <span className="shuttle-tm-marker-label">
                          {schedule.time}
                          {count > 1 && <span className="shuttle-tm-marker-count">×{count}</span>}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Playback Controls & Steps */}
            <div className="shuttle-tm-control-section">
              <div className="shuttle-tm-playback-bar">
                <button
                  type="button"
                  className="shuttle-tm-step-btn"
                  onClick={() => handleStepSeconds(-600)}
                  title="10분 뒤로"
                >
                  -10분
                </button>
                <button
                  type="button"
                  className="shuttle-tm-step-btn"
                  onClick={() => handleStepSeconds(-60)}
                  title="1분 뒤로"
                >
                  -1분
                </button>
                <button
                  type="button"
                  className="shuttle-tm-step-btn sub"
                  onClick={() => handleStepSeconds(-10)}
                  title="10초 뒤로"
                >
                  -10초
                </button>

                <button
                  type="button"
                  className={`shuttle-tm-play-btn${isPlaying ? ' is-playing' : ''}`}
                  onClick={() => setIsPlaying(p => !p)}
                  title={isPlaying ? '일시 정지' : '실시간 재생'}
                >
                  {isPlaying ? <Pause size={17} /> : <Play size={17} />}
                  <span>{isPlaying ? '일시정지' : '재생'}</span>
                </button>

                <button
                  type="button"
                  className="shuttle-tm-step-btn sub"
                  onClick={() => handleStepSeconds(10)}
                  title="10초 앞으로"
                >
                  +10초
                </button>
                <button
                  type="button"
                  className="shuttle-tm-step-btn"
                  onClick={() => handleStepSeconds(60)}
                  title="1분 앞으로"
                >
                  +1분
                </button>
                <button
                  type="button"
                  className="shuttle-tm-step-btn"
                  onClick={() => handleStepSeconds(600)}
                  title="10분 앞으로"
                >
                  +10분
                </button>
              </div>

              {/* Speed Selectors */}
              <div className="shuttle-tm-speed-row">
                <span className="shuttle-tm-speed-label">
                  <FastForward size={13} />
                  재생 속도
                </span>
                <div className="shuttle-tm-speed-group" role="group" aria-label="재생 속도">
                  {SPEED_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`shuttle-tm-speed-btn${playbackSpeed === opt.value ? ' active' : ''}`}
                      onClick={() => setPlaybackSpeed(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Dynamic Checkpoint Jump Buttons */}
            {dynamicCheckpoints.length > 0 && (
              <div className="shuttle-tm-control-section">
                <span className="shuttle-tm-section-label">
                  <Zap size={13} />
                  주요 체크포인트 바로가기
                </span>
                <div className="shuttle-tm-checkpoints-grid">
                  {dynamicCheckpoints.map((cp, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="shuttle-tm-checkpoint-btn"
                      onClick={() => setSimulatedSeconds(cp.timeSec)}
                    >
                      {cp.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Admin Developer Meta Status Grid */}
            <div className="shuttle-tm-meta-grid" aria-label="개발자 런타임 상태 요약">
              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">현재 시각</span>
                <strong className="shuttle-tm-meta-val primary">{secToTime(simulatedSeconds)}</strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">다음 배차</span>
                <strong className="shuttle-tm-meta-val">{liveDepartureTime ?? '운행 없음'}</strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">남은 시간</span>
                <strong className="shuttle-tm-meta-val highlight">
                  {liveSecondsUntilDeparture != null
                    ? liveSecondsUntilDeparture > 0
                      ? liveSecondsUntilDeparture >= 60
                        ? `${Math.floor(liveSecondsUntilDeparture / 60)}분 ${liveSecondsUntilDeparture % 60}초`
                        : `${liveSecondsUntilDeparture}초`
                      : liveMinsUntilDeparture <= 0
                        ? '출발 중'
                        : '--'
                    : isLiveEnded
                      ? '운행 종료'
                      : '--'}
                </strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">상태 (Phase)</span>
                <strong className="shuttle-tm-meta-val">
                  <span className={`shuttle-qa-phase-badge phase-${liveVisualPhase}`}>
                    {liveVisualPhase}
                  </span>
                </strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">모드 (Mode)</span>
                <strong className="shuttle-tm-meta-val">{liveMotionMode}</strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">배차 대수</span>
                <strong className="shuttle-tm-meta-val">{liveBusCount}대</strong>
              </div>

              <div className="shuttle-tm-meta-item">
                <span className="shuttle-tm-meta-key">다음 편 (Following)</span>
                <strong className="shuttle-tm-meta-val">{liveFollowingSchedule?.time ?? '--'}</strong>
              </div>

              <div className="shuttle-tm-meta-item full">
                <span className="shuttle-tm-meta-key">동기화 상태</span>
                <span className="shuttle-tm-sync-ok">
                  <CheckCircle2 size={13} />
                  Home Hero & Shuttle Page Hero 100% 동일 State 동기화 중
                </span>
              </div>
            </div>
          </div>
        </aside>
      </main>

      {/* ── Preserved 12 Fixed QA Scenarios (Collapsible) ──────── */}
      <section className="shuttle-qa-legacy-section" aria-label="고정 QA 체크포인트">
        <button
          type="button"
          className="shuttle-qa-legacy-toggle"
          onClick={() => setIsLegacyOpen(open => !open)}
          aria-expanded={isLegacyOpen}
        >
          <div className="shuttle-qa-legacy-toggle-title">
            <strong>자동 QA 고정 시나리오 ({scenarios.length}개)</strong>
            <span className="shuttle-qa-legacy-toggle-desc">
              회귀 방지용 엣지케이스 고정 검증 체크포인트 목록
            </span>
          </div>
          {isLegacyOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {isLegacyOpen && (
          <div className="shuttle-qa-matrix">
            {scenarios.map(scenario => (
              <ScenarioCard
                key={`${selectedRoute?.id}-${scenario.id}`}
                scenario={scenario}
                route={selectedRoute}
                targetView={targetView}
                isDark={isDark}
                viewportWidth={currentViewportWidth}
                replayEpoch={replayEpoch}
              />
            ))}
          </div>
        )}
      </section>
    </section>
  )
}
